import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { makeStyles, mergeClasses, tokens, Button, FluentProvider } from '@fluentui/react-components';
import logo from '../assets/img/logo.svg';
import { useAuth } from '../context/AuthContext';
import Login from '../components/Login';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { iconMap } from '../theme/icons';
import { fititLightTheme } from '../theme/brand';
import imgWardrobeHinged from '../assets/img/marketing/cat-wardrobe-hinged.jpg';
import imgWardrobeSliding from '../assets/img/marketing/cat-wardrobe-sliding.jpg';
import imgDresser from '../assets/img/marketing/cat-dresser.jpg';
import imgShelving from '../assets/img/marketing/cat-shelving.jpg';
import imgTvStand from '../assets/img/marketing/cat-tv-stand.jpg';
import imgCabinet from '../assets/img/marketing/cat-cabinet.jpg';

const PhoneIcon = iconMap.phone;
const LocationIcon = iconMap.location_on;
const ChevronDownIcon = iconMap.expand_more;
const LoginIcon = iconMap.login;
const AccountIcon = iconMap.person;
const CartIcon = iconMap.shopping_cart;
const CloseIcon = iconMap.close;
const MenuIcon = iconMap.menu;
const ConfiguratorIcon = iconMap.view_in_ar;
const ArrowForwardIcon = iconMap.arrow_forward;

const NAV_LINKS = [
  { to: '/siusti-uzklausa', labelKey: 'nav.send_request' },
  { to: '/konfiguratorius', labelKey: 'nav.configurator' },
];

const CATEGORY_LINKS = [
  { to: '/baldai/spintos-varstomomis-durimis', labelKey: 'nav.cat_wardrobe_hinged', img: imgWardrobeHinged },
  { to: '/baldai/spintos-stumdomomis-durimis', labelKey: 'nav.cat_wardrobe_sliding', img: imgWardrobeSliding },
  { to: '/baldai/komodos', labelKey: 'nav.cat_dresser', img: imgDresser },
  { to: '/baldai/lentynos', labelKey: 'nav.cat_shelving', img: imgShelving },
  { to: '/baldai/tv-spinteles', labelKey: 'nav.cat_tv_stand', img: imgTvStand },
  { to: '/baldai/spinteles', labelKey: 'nav.cat_cabinet', img: imgCabinet },
];

const INFO_LINKS = [
  { to: '/galerija', labelKey: 'nav.info_gallery', icon: 'photo_library' },
  { to: '/duk', labelKey: 'nav.info_faq', icon: 'help_outline' },
  { to: '/ekspozicija', labelKey: 'nav.info_showroom', icon: 'store' },
  { to: '/meistro-uzsakymas', labelKey: 'nav.info_carpenter', icon: 'handyman' },
  { to: '/dovanu-cekis', labelKey: 'nav.info_gift_card', icon: 'card_giftcard' },
];

// Tikslūs `gray-150`/`bg-white/80`/backdrop-blur/šešėlių atspalviai originaliame
// dizaine neturi 1:1 semantinio Fluent token'o (dekoratyvus permatomumas
// slenkant), tad lieka literalūs — likusios spalvos/tarpai/šriftai naudoja
// tokens.* (žr. NavigationBar.jsx paterną).
const useStyles = makeStyles({

  header: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    transitionProperty: 'background-color, box-shadow, border-color, backdrop-filter',
    transitionDuration: tokens.durationNormal,
  },
  // TopBar.jsx (virš šio, tame pačiame sticky header'yje namų puslapyje) neturi
  // atitinkamos "scrolled" būsenos — jo fonas visada solidus colorNeutralBackground1.
  // Anksčiau ČIA scrolinant fonas tapdavo permatomas (rgba(255,255,255,0.8) +
  // blur), tad slenkant matėsi AIŠKUS lūžis tarp solidaus TopBar ir permatomo
  // MainMenu tame pačiame prilipusiame header'yje (žr. /admin/bugs "Scrolinant i
  // apacia header turi buti vienodas background, nes dabar skiriasi kur yra
  // kokybes garantija nuoroda ir logotipas"). Dabar solidus, TAS PATS fonas kaip
  // įprastai — tik šešėlis/borderis rodo scroll būseną.
  headerScrolled: {
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow4,
    borderBottomColor: tokens.colorNeutralStroke3,
  },

  container: {
    maxWidth: '80rem',
    marginLeft: 'auto',
    marginRight: 'auto',
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    // Turi tiksliai sutapti su puslapio turinio konteinerių paddingu — DAUGUMA
    // puslapių (Home/FurnitureItem/SendRequest/Duk/... — žr. /admin/bugs "Header
    // logotipas ir language icon turi lygiuoti su puslapio turiniu") naudoja
    // literalų 48px, NE tokens.spacingHorizontalXXXL (32px, mažumos atvejis).
    '@media (min-width: 1024px)': { paddingLeft: '3rem', paddingRight: '3rem' },
  },
  row: { display: 'flex', alignItems: 'center', height: '64px', gap: tokens.spacingHorizontalL },

  logoLink: { display: 'flex', alignItems: 'center', flexShrink: 0, gap: '10px', marginRight: tokens.spacingHorizontalXS, textDecorationLine: 'none' },

  contactBlock: {
    display: 'none',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    '@media (min-width: 1280px)': { display: 'flex' },
  },
  phoneLink: { display: 'flex', alignItems: 'center', gap: '10px', textDecorationLine: 'none' },
  brandIcon: { color: tokens.colorBrandForeground2, fontSize: '22px' },
  phoneTextWrap: { whiteSpace: 'nowrap' },
  phoneNumber: { fontWeight: tokens.fontWeightSemibold, color: tokens.colorNeutralForeground1, lineHeight: '1.2', fontSize: '13px' },
  phoneEmail: { fontSize: '11px', color: tokens.colorBrandForeground2, lineHeight: '1.2' },
  verticalDivider: { width: '1px', height: '32px', backgroundColor: tokens.colorNeutralStroke2 },
  locationBlock: { display: 'flex', alignItems: 'center', gap: '10px' },
  locationLabel: { fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground4, lineHeight: '1.2', textTransform: 'uppercase', letterSpacing: '0.025em' },
  locationValue: { fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, lineHeight: '1.2', fontWeight: tokens.fontWeightMedium },

  desktopNav: { display: 'none', alignItems: 'center', '@media (min-width: 768px)': { display: 'flex' } },

  infoWrap: { position: 'relative' },
  infoButton: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    backgroundColor: 'transparent',
    border: 'none',
    fontFamily: 'inherit',
    cursor: 'pointer',
    color: tokens.colorNeutralForeground2,
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    ':hover': { color: tokens.colorBrandForeground2 },
    ':hover > span:last-child': { transform: 'scaleX(1)' },
  },
  infoButtonActive: { color: tokens.colorBrandForeground2 },
  infoChevron: { fontSize: tokens.fontSizeBase400, transitionProperty: 'transform', transitionDuration: tokens.durationFast },
  infoChevronOpen: { transform: 'rotate(180deg)' },

  underline: {
    pointerEvents: 'none',
    position: 'absolute',
    bottom: 0,
    left: tokens.spacingHorizontalL,
    right: tokens.spacingHorizontalL,
    height: '2px',
    backgroundColor: tokens.colorBrandForeground2,
    transitionProperty: 'transform',
    transitionDuration: tokens.durationFast,
    transformOrigin: 'left',
    transform: 'scaleX(0)',
  },
  underlineActive: { transform: 'scaleX(1)' },

  infoMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 0,
    width: '224px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    zIndex: tokens.zIndexPopup,
  },

  // ── "Baldai" mega-meniu — pakeičia buvusią atskirą tamsią kategorijų juostą
  // (NavigationBar.jsx) viena iškrentančia dviejų stulpelių tinklele, sumažinant
  // antraštę iš 3 sudėtų juostų iki 2 ir suteikiant daugiau vietos turiniui.
  catalogMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 0,
    width: '380px',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    padding: tokens.spacingHorizontalM,
    zIndex: tokens.zIndexPopup,
  },
  catalogGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalXXS },
  catalogItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusXLarge,
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  },
  catalogItemThumb: {
    width: '40px', height: '40px', borderRadius: tokens.borderRadiusLarge, flexShrink: 0,
    objectFit: 'cover', border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  catalogItemLabel: { fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightMedium, lineHeight: '1.25' },
  catalogFooterLink: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: tokens.spacingHorizontalXXS,
    marginTop: tokens.spacingVerticalXS, paddingTop: tokens.spacingVerticalS, paddingBottom: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    fontSize: tokens.fontSizeBase200, fontWeight: tokens.fontWeightSemibold, color: tokens.colorBrandForegroundLink,
    textDecorationLine: 'none',
    ':hover': { color: tokens.colorBrandForegroundLinkHover },
  },
  infoMenuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  },
  infoMenuItemIcon: { color: tokens.colorNeutralForeground4, fontSize: tokens.fontSizeBase400 },

  navLink: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    ':hover': { color: tokens.colorBrandForeground2 },
    ':hover > span:last-child': { transform: 'scaleX(1)' },
  },
  navLinkActive: { color: tokens.colorBrandForeground2 },

  spacer: { flex: 1 },

  iconsRow: { display: 'flex', alignItems: 'center' },
  iconBtn: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    color: tokens.colorNeutralForeground3,
    textDecorationLine: 'none',
    fontSize: tokens.fontSizeBase500,
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    ':hover': { color: tokens.colorBrandForeground2 },
  },
  iconBtnReset: { backgroundColor: 'transparent', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit' },

  cartBadge: {
    position: 'absolute',
    top: '-2px',
    right: '-2px',
    minWidth: '16px',
    height: '16px',
    backgroundColor: tokens.colorPaletteRedBackground3,
    color: tokens.colorNeutralForegroundStaticInverted,
    fontSize: '9px',
    fontWeight: tokens.fontWeightBold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: tokens.borderRadiusCircular,
    paddingLeft: '2px',
    paddingRight: '2px',
  },

  languageWrap: { width: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' },

  hamburgerBtn: {
    display: 'flex',
    marginLeft: tokens.spacingHorizontalXS,
    fontSize: '22px',
    '@media (min-width: 768px)': { display: 'none' },
  },

  mobileOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: tokens.zIndexOverlay,
    backgroundColor: 'rgba(0,0,0,0.55)',
    backdropFilter: 'blur(2px)',
  },

  sidenav: {
    position: 'fixed',
    top: 0,
    right: 0,
    height: '100%',
    zIndex: tokens.zIndexPopup,
    backgroundColor: tokens.colorNeutralBackground1,
    display: 'flex',
    flexDirection: 'column',
  },
  sidenavHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingLeft: tokens.spacingHorizontalXL,
    paddingRight: tokens.spacingHorizontalXL,
    height: '64px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    flexShrink: 0,
  },
  sidenavCloseBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    color: tokens.colorNeutralForeground4,
    fontSize: '22px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    ':hover': { color: tokens.colorNeutralForeground2 },
  },
  sidenavNav: {
    flex: 1,
    overflowY: 'auto',
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  sidenavSectionLabel: {
    marginBottom: tokens.spacingVerticalXXS,
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground4,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  sidenavSectionLabelSpaced: { marginTop: tokens.spacingVerticalM },

  sidenavLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightMedium,
    borderRadius: tokens.borderRadius2XLarge,
    marginBottom: '2px',
    color: tokens.colorNeutralForeground1,
    textDecorationLine: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorBrandForeground2 },
  },
  sidenavCategoryLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadius2XLarge,
    marginBottom: '2px',
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorBrandForeground2 },
  },
  sidenavInfoLink: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalSNudge,
    paddingBottom: tokens.spacingVerticalSNudge,
    fontSize: tokens.fontSizeBase300,
    borderRadius: tokens.borderRadius2XLarge,
    marginBottom: '2px',
    color: tokens.colorNeutralForeground2,
    textDecorationLine: 'none',
    transitionProperty: 'background-color, color',
    transitionDuration: tokens.durationFast,
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorBrandForeground2 },
  },
  sidenavLinkActive: { backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  sidenavLinkIcon16: { color: tokens.colorNeutralForeground4, fontSize: tokens.fontSizeBase400 },
  sidenavCategoryThumb: {
    width: '28px', height: '28px', borderRadius: tokens.borderRadiusMedium, flexShrink: 0,
    objectFit: 'cover', border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  sidenavLinkIcon17: { color: tokens.colorNeutralForeground4, fontSize: '17px' },

  sidenavFooter: {
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalM,
    paddingBottom: tokens.spacingVerticalM,
    borderTop: `1px solid ${tokens.colorNeutralStroke3}`,
    flexShrink: 0,
  },
  // Realus Fluent <Button appearance="primary"> jau suteikia aukštį/šriftą/
  // kampų apvalinimą pagal Fluent2 standartą (žr. 18 punktų sąrašo #5) —
  // čia tik plotis per visą sidenav pločio konteinerį.
  ctaButton: { width: '100%' },
  footerPhoneLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalS,
    marginTop: tokens.spacingVerticalSNudge,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    textDecorationLine: 'none',
    transitionProperty: 'color',
    transitionDuration: tokens.durationFast,
    ':hover': { color: tokens.colorBrandForeground2 },
  },
});

function MainMenu() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const infoRef = useRef(null);
  const catalogRef = useRef(null);
  const s = useStyles();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    const updateCart = () => setCartItems(JSON.parse(sessionStorage.getItem('furnitureCart')) || []);
    window.addEventListener('scroll', onScroll);
    window.addEventListener('cartUpdated', updateCart);
    window.addEventListener('storage', updateCart);
    updateCart();
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('cartUpdated', updateCart);
      window.removeEventListener('storage', updateCart);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfoOpen(false);
      if (catalogRef.current && !catalogRef.current.contains(e.target)) setCatalogOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { setMobileOpen(false); setInfoOpen(false); setCatalogOpen(false); }, [location]);

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <>
      <header className={mergeClasses(s.header, scrolled && s.headerScrolled)}>
        <div className={s.container}>
          <div className={s.row}>

            {/* Logo */}
            <Link to="/" className={s.logoLink}>
              <img src={logo} alt="Fitit" style={{ height: 26, width: 'auto' }} />
            </Link>

            {/* Contact block — xl+ only */}
            <div className={s.contactBlock}>
              <a href="tel:+37060517874" className={s.phoneLink}>
                <PhoneIcon className={s.brandIcon} />
                <div className={s.phoneTextWrap}>
                  <div className={s.phoneNumber}>+370 605 17874</div>
                  <div className={s.phoneEmail}>info@fitit.lt</div>
                </div>
              </a>
              <div className={s.verticalDivider} />
              <div className={s.locationBlock}>
                <LocationIcon className={s.brandIcon} />
                <div>
                  <div className={s.locationLabel}>{t('nav.showroom')}</div>
                  <div className={s.locationValue}>{t('nav.showroom_cities')}</div>
                </div>
              </div>
            </div>

            {/* Desktop nav */}
            <nav className={s.desktopNav}>
              {/* Baldai mega-meniu — pakeičia buvusią atskirą tamsią kategorijų juostą */}
              <div className={s.infoWrap} ref={catalogRef}>
                <button
                  onClick={() => setCatalogOpen(!catalogOpen)}
                  className={mergeClasses(s.infoButton, catalogOpen && s.infoButtonActive)}
                >
                  {t('nav.furniture')}
                  <ChevronDownIcon className={mergeClasses(s.infoChevron, catalogOpen && s.infoChevronOpen)} />
                  <span className={mergeClasses(s.underline, catalogOpen && s.underlineActive)} />
                </button>

                {catalogOpen && (
                  <div className={s.catalogMenu}>
                    <div className={s.catalogGrid}>
                      {CATEGORY_LINKS.map(({ to, labelKey, img }) => (
                        <Link key={to} to={to} className={s.catalogItem}>
                          <img src={img} alt="" className={s.catalogItemThumb} />
                          <span className={s.catalogItemLabel}>{t(labelKey)}</span>
                        </Link>
                      ))}
                    </div>
                    <Link to="/baldai" className={s.catalogFooterLink}>
                      {t('nav.all_furniture')}
                      <ArrowForwardIcon fontSize={14} />
                    </Link>
                  </div>
                )}
              </div>

              {/* Informacija dropdown */}
              <div className={s.infoWrap} ref={infoRef}>
                <button
                  onClick={() => setInfoOpen(!infoOpen)}
                  className={mergeClasses(s.infoButton, infoOpen && s.infoButtonActive)}
                >
                  {t('nav.information')}
                  <ChevronDownIcon className={mergeClasses(s.infoChevron, infoOpen && s.infoChevronOpen)} />
                  <span className={mergeClasses(s.underline, infoOpen && s.underlineActive)} />
                </button>

                {infoOpen && (
                  <div className={s.infoMenu}>
                    {INFO_LINKS.map(({ to, labelKey, icon }) => {
                      const ItemIcon = iconMap[icon];
                      return (
                        <Link key={to} to={to} className={s.infoMenuItem}>
                          <ItemIcon className={s.infoMenuItemIcon} />
                          {t(labelKey)}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>

              {NAV_LINKS.map(({ to, labelKey }) => (
                <Link key={to} to={to} className={mergeClasses(s.navLink, isActive(to) && s.navLinkActive)}>
                  {t(labelKey)}
                  <span className={mergeClasses(s.underline, isActive(to) && s.underlineActive)} />
                </Link>
              ))}
            </nav>

            {/* Spacer */}
            <div className={s.spacer} />

            {/* Right icons */}
            <div className={s.iconsRow}>
              {!isAuthenticated ? (
                <button
                  onClick={() => setLoginOpen(true)}
                  className={mergeClasses(s.iconBtn, s.iconBtnReset)}
                  title={t('nav.login')}
                  aria-label={t('nav.login')}
                >
                  <LoginIcon />
                </button>
              ) : (
                <Link to="/admin/orders" className={s.iconBtn} title={t('nav.account')}>
                  <AccountIcon />
                </Link>
              )}

              <Link to="/cart" className={s.iconBtn} title={t('nav.cart')}>
                <CartIcon />
                {cartItems.length > 0 && (
                  <span className={s.cartBadge}>{cartItems.length}</span>
                )}
              </Link>

              <div className={s.languageWrap}>
                <LanguageSwitcher />
              </div>

              {/* Mobile hamburger */}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className={mergeClasses(s.iconBtn, s.iconBtnReset, s.hamburgerBtn)}
                aria-label={mobileOpen ? t('nav.close_menu') : t('nav.open_menu')}
              >
                {mobileOpen ? <CloseIcon /> : <MenuIcon />}
              </button>
            </div>
          </div>
        </div>

      </header>

      {/* Mobile sidenav overlay + panel — PORTALINAMA tiesiai į document.body, NE
          vaikas <header> viduje. <header> gauna backdropFilter:'blur()' (žr.
          headerScrolled aukščiau) kai tik puslapis paslenkamas >8px — CSS
          backdrop-filter/filter ant BET KOKIO protėvio sukuria NAUJĄ "containing
          block" position:fixed palikuonims, tad .sidenav (position:fixed,
          height:'100%') savo aukštį pradėdavo skaičiuoti NE nuo viewport, o nuo
          PAČIO <header> (vos ~64-120px), ir sugriūdavo į plyšelį vos paslinkus
          puslapį — žr. /admin/bugs "Atsidarius menu ir nuskrolinus i apacia
          sugriuna desines puses menu vaizdas". TA PATI klaidų klasė jau kartą
          spręsta admin pusėje (žr. theme/primitives.jsx SimpleModal komentarą
          "sidenav-po-backdrop") tuo pačiu būdu — portalas pilnai apeina protėvio
          stacking/containing-block kontekstą. FluentProvider čia BŪTINAS —
          portalintas turinys atsiduria UŽ pagrindinio FluentProvider React
          pomedžio, tad tokens.* CSS kintamieji jame liktų neapibrėžti be savo
          temos konteksto (ta pati priežastis, dėl ko SimpleModal tai daro). */}
      {createPortal((
        <FluentProvider theme={fititLightTheme} style={{ background: 'transparent' }}>
          {mobileOpen && (
            <div className={s.mobileOverlay} onClick={() => setMobileOpen(false)} />
          )}
          <div
            className={s.sidenav}
            style={{
              width: 280,
              transform: mobileOpen ? 'translateX(0)' : 'translateX(100%)',
              transition: 'transform 0.28s cubic-bezier(0.4,0,0.2,1)',
              boxShadow: mobileOpen ? '-4px 0 24px rgba(0,0,0,0.12)' : 'none',
            }}
          >
            {/* Sidenav header */}
            <div className={s.sidenavHeader}>
              <img src={logo} alt="Fitit" style={{ height: 22, width: 'auto' }} />
              <button onClick={() => setMobileOpen(false)} className={s.sidenavCloseBtn} aria-label={t('nav.close_menu')}>
                <CloseIcon />
              </button>
            </div>

            {/* Sidenav links */}
            <nav className={s.sidenavNav}>
              <div className={s.sidenavSectionLabel}>{t('nav.menu')}</div>
              {NAV_LINKS.map(({ to, labelKey }) => (
                <Link
                  key={to}
                  to={to}
                  className={mergeClasses(s.sidenavLink, isActive(to) && s.sidenavLinkActive)}
                >
                  {t(labelKey)}
                </Link>
              ))}

              <div className={mergeClasses(s.sidenavSectionLabel, s.sidenavSectionLabelSpaced)}>{t('nav.catalogs')}</div>
              {CATEGORY_LINKS.map(({ to, labelKey, img }) => (
                <Link
                  key={to}
                  to={to}
                  className={mergeClasses(s.sidenavCategoryLink, isActive(to) && s.sidenavLinkActive)}
                >
                  <img src={img} alt="" className={s.sidenavCategoryThumb} />
                  {t(labelKey)}
                </Link>
              ))}

              <div className={mergeClasses(s.sidenavSectionLabel, s.sidenavSectionLabelSpaced)}>{t('nav.information')}</div>
              {INFO_LINKS.map(({ to, labelKey, icon }) => {
                const ItemIcon = iconMap[icon];
                return (
                  <Link
                    key={to}
                    to={to}
                    className={mergeClasses(s.sidenavInfoLink, isActive(to) && s.sidenavLinkActive)}
                  >
                    <ItemIcon className={s.sidenavLinkIcon17} />
                    {t(labelKey)}
                  </Link>
                );
              })}
            </nav>

            {/* Sidenav footer */}
            <div className={s.sidenavFooter}>
              <Button
                appearance="primary"
                size="large"
                as={Link}
                to="/konfiguratorius"
                icon={<ConfiguratorIcon style={{ fontSize: 18 }} />}
                className={s.ctaButton}
              >
                {t('nav.open_configurator')}
              </Button>
              <a href="tel:+37060517874" className={s.footerPhoneLink}>
                <PhoneIcon style={{ fontSize: 17 }} />
                +370 605 17874
              </a>
            </div>
          </div>
        </FluentProvider>
      ), document.body)}

      <Login open={loginOpen} onClose={() => setLoginOpen(false)} />
    </>
  );
}

export default MainMenu;
